/**
 * ConditionTemplatePage — Landlord Portal (JR-1)
 *
 * Per-unit condition-checklist template editor: instantiate the room-type default
 * catalog, add/remove/edit rooms and items, and capture appliance make/model/serial.
 * Mobile-first: rooms stack as cards, items as compact rows within each room.
 *
 * Route: mounted at a unit-scoped path with a `unitId` param (e.g.
 * `/units/:unitId/condition-template`) — matches `UnitDetailPage`'s param convention.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Sparkles, Home } from 'lucide-react';
import {
  getConditionTemplate,
  instantiateConditionTemplate,
  createTemplateRoom,
  updateTemplateRoom,
  deleteTemplateRoom,
  createTemplateItem,
  updateTemplateItem,
  deleteTemplateItem,
  getRoomTypeDefaults,
  ROOM_TYPE_LABELS,
  ITEM_CATEGORY_LABELS,
  type UnitRoomResponse,
  type UnitRoomItemResponse,
  type RoomType,
  type ItemCategory,
  type RoomTypeDefault,
} from '../lib/api';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader, EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const ROOM_TYPE_OPTIONS = Object.keys(ROOM_TYPE_LABELS) as RoomType[];
const ITEM_CATEGORY_OPTIONS = Object.keys(ITEM_CATEGORY_LABELS) as ItemCategory[];

const inputClass =
  'w-full px-3 py-2.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring box-border bg-background';
const labelClass = 'block mb-1 text-sm font-medium text-foreground';

export function ConditionTemplatePage() {
  const { unitId } = useParams<{ unitId: string }>();

  const [rooms, setRooms] = useState<UnitRoomResponse[]>([]);
  const [defaults, setDefaults] = useState<RoomTypeDefault[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInstantiating, setIsInstantiating] = useState(false);

  const [isAddRoomOpen, setIsAddRoomOpen] = useState(false);
  const [roomForm, setRoomForm] = useState<{ roomType: RoomType | ''; name: string; populateDefaults: boolean }>({
    roomType: '',
    name: '',
    populateDefaults: true,
  });
  const [isSavingRoom, setIsSavingRoom] = useState(false);
  const [roomFormError, setRoomFormError] = useState<string | null>(null);

  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [editingRoomName, setEditingRoomName] = useState('');

  const [roomPendingDelete, setRoomPendingDelete] = useState<UnitRoomResponse | null>(null);
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);

  const [addItemRoomId, setAddItemRoomId] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState({ name: '', category: '' as ItemCategory | '', make: '', model: '', serialNumber: '' });
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [itemFormError, setItemFormError] = useState<string | null>(null);

  const [editingItem, setEditingItem] = useState<UnitRoomItemResponse | null>(null);
  const [itemPendingDelete, setItemPendingDelete] = useState<UnitRoomItemResponse | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  const fetchData = useCallback(async () => {
    if (!unitId) return;
    try {
      setIsLoading(true);
      setError(null);
      const [templateRes, defaultsRes] = await Promise.all([
        getConditionTemplate(unitId),
        getRoomTypeDefaults(),
      ]);
      setRooms(templateRes.rooms);
      setDefaults(defaultsRes.defaults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load condition template');
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeRooms = useMemo(
    () => rooms.filter((r) => !r.deletedAt).sort((a, b) => a.displayOrder - b.displayOrder),
    [rooms]
  );

  const handleInstantiate = async () => {
    if (!unitId) return;
    setIsInstantiating(true);
    setError(null);
    try {
      const result = await instantiateConditionTemplate(unitId);
      setRooms(result.rooms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed default rooms');
    } finally {
      setIsInstantiating(false);
    }
  };

  const openAddRoom = () => {
    setRoomForm({ roomType: '', name: '', populateDefaults: true });
    setRoomFormError(null);
    setIsAddRoomOpen(true);
  };

  const handleRoomTypeSelect = (roomType: RoomType) => {
    const preset = defaults.find((d) => d.roomType === roomType);
    setRoomForm({
      ...roomForm,
      roomType,
      name: roomForm.name || ROOM_TYPE_LABELS[roomType] || preset?.roomType || '',
    });
  };

  // Room type must be selected and carry at least one catalog default item (e.g. OTHER
  // has none) for the checkbox to be actionable — BR-25/US-19 no-op for no-default types.
  const selectedRoomTypeDefault = roomForm.roomType ? defaults.find((d) => d.roomType === roomForm.roomType) : undefined;
  const selectedRoomTypeHasDefaults = !!selectedRoomTypeDefault?.defaultItems.length;
  const effectivePopulateDefaults = selectedRoomTypeHasDefaults && roomForm.populateDefaults;

  const handleAddRoomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unitId || !roomForm.roomType || !roomForm.name.trim()) return;
    setIsSavingRoom(true);
    setRoomFormError(null);
    try {
      const { room } = await createTemplateRoom(unitId, {
        roomType: roomForm.roomType,
        name: roomForm.name.trim(),
        displayOrder: activeRooms.length,
        populateDefaults: effectivePopulateDefaults,
      });
      setRooms((prev) => [...prev, room]);
      setIsAddRoomOpen(false);
    } catch (err) {
      setRoomFormError(err instanceof Error ? err.message : 'Failed to add room');
    } finally {
      setIsSavingRoom(false);
    }
  };

  const startEditRoom = (room: UnitRoomResponse) => {
    setEditingRoomId(room.id);
    setEditingRoomName(room.name);
  };

  const saveRoomName = async (roomId: string) => {
    const trimmed = editingRoomName.trim();
    if (!trimmed) return;
    try {
      const { room } = await updateTemplateRoom(roomId, { name: trimmed });
      setRooms((prev) => prev.map((r) => (r.id === roomId ? room : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename room');
    } finally {
      setEditingRoomId(null);
    }
  };

  const confirmDeleteRoom = async () => {
    if (!roomPendingDelete) return;
    setIsDeletingRoom(true);
    try {
      await deleteTemplateRoom(roomPendingDelete.id);
      setRooms((prev) => prev.filter((r) => r.id !== roomPendingDelete.id));
      setRoomPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete room');
    } finally {
      setIsDeletingRoom(false);
    }
  };

  const openAddItem = (roomId: string) => {
    setAddItemRoomId(roomId);
    setItemForm({ name: '', category: '', make: '', model: '', serialNumber: '' });
    setItemFormError(null);
  };

  const handleAddItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addItemRoomId || !itemForm.category || !itemForm.name.trim()) return;
    setIsSavingItem(true);
    setItemFormError(null);
    try {
      const room = rooms.find((r) => r.id === addItemRoomId);
      const { item } = await createTemplateItem(addItemRoomId, {
        name: itemForm.name.trim(),
        category: itemForm.category,
        make: itemForm.make.trim() || undefined,
        model: itemForm.model.trim() || undefined,
        serialNumber: itemForm.serialNumber.trim() || undefined,
        displayOrder: room?.items.length ?? 0,
      });
      setRooms((prev) =>
        prev.map((r) => (r.id === addItemRoomId ? { ...r, items: [...r.items, item] } : r))
      );
      setAddItemRoomId(null);
    } catch (err) {
      setItemFormError(err instanceof Error ? err.message : 'Failed to add item');
    } finally {
      setIsSavingItem(false);
    }
  };

  const startEditItem = (item: UnitRoomItemResponse) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      category: item.category,
      make: item.make ?? '',
      model: item.model ?? '',
      serialNumber: item.serialNumber ?? '',
    });
    setItemFormError(null);
  };

  const handleEditItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !itemForm.category || !itemForm.name.trim()) return;
    setIsSavingItem(true);
    setItemFormError(null);
    try {
      const { item } = await updateTemplateItem(editingItem.id, {
        name: itemForm.name.trim(),
        category: itemForm.category,
        make: itemForm.make.trim() || null,
        model: itemForm.model.trim() || null,
        serialNumber: itemForm.serialNumber.trim() || null,
      });
      setRooms((prev) =>
        prev.map((r) => ({ ...r, items: r.items.map((i) => (i.id === item.id ? item : i)) }))
      );
      setEditingItem(null);
    } catch (err) {
      setItemFormError(err instanceof Error ? err.message : 'Failed to update item');
    } finally {
      setIsSavingItem(false);
    }
  };

  const confirmDeleteItem = async () => {
    if (!itemPendingDelete) return;
    setIsDeletingItem(true);
    try {
      await deleteTemplateItem(itemPendingDelete.id);
      setRooms((prev) =>
        prev.map((r) => ({ ...r, items: r.items.filter((i) => i.id !== itemPendingDelete.id) }))
      );
      setItemPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setIsDeletingItem(false);
    }
  };

  if (!unitId) {
    return (
      <div className="px-[var(--page-padding-x)] py-8" data-testid="condition-template-page">
        <p className="text-sm text-destructive" role="alert">Missing unit.</p>
      </div>
    );
  }

  if (isLoading) {
    return <ConditionTemplatePageSkeleton />;
  }

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="condition-template-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Condition Template"
          subtitle={`${activeRooms.length} room${activeRooms.length !== 1 ? 's' : ''}`}
          backLink={{ label: 'Unit', to: `/units/${unitId}` }}
          action={
            <Button size="sm" onClick={openAddRoom} data-testid="add-room-button">
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Add Room</span>
            </Button>
          }
        />
      </div>

      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="error-message">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-2">Retry</Button>
        </div>
      )}

      {activeRooms.length === 0 ? (
        <EmptyState
          icon={Home}
          title="No Rooms Yet"
          description="Seed the standard room/item catalog for this unit, or add rooms manually."
          data-testid="condition-template-empty-state"
          action={
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleInstantiate} disabled={isInstantiating} data-testid="instantiate-defaults-button">
                <Sparkles className="h-4 w-4 mr-1" />
                {isInstantiating ? 'Seeding…' : 'Use Default Rooms'}
              </Button>
              <Button variant="outline" onClick={openAddRoom}>Add Room Manually</Button>
            </div>
          }
        />
      ) : (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-3" data-testid="condition-template-rooms-list">
          {activeRooms.map((room) => (
            <div key={room.id} className="rounded-lg border border-border bg-card" data-testid={`room-card-${room.id}`}>
              <div className="flex items-center justify-between gap-2 p-3 border-b border-border">
                {editingRoomId === room.id ? (
                  <form
                    className="flex-1 flex items-center gap-2"
                    onSubmit={(e) => { e.preventDefault(); saveRoomName(room.id); }}
                  >
                    <label htmlFor={`room-name-${room.id}`} className="sr-only">Room name</label>
                    <input
                      id={`room-name-${room.id}`}
                      data-testid={`room-name-input-${room.id}`}
                      autoFocus
                      className={inputClass}
                      value={editingRoomName}
                      onChange={(e) => setEditingRoomName(e.target.value)}
                      onBlur={() => saveRoomName(room.id)}
                    />
                    <Button type="submit" size="sm" data-testid={`room-name-save-${room.id}`}>Save</Button>
                  </form>
                ) : (
                  <div className="min-w-0 flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{room.name}</p>
                    <Badge variant="outline" className="shrink-0 text-xs">{ROOM_TYPE_LABELS[room.roomType]}</Badge>
                  </div>
                )}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    aria-label={`Rename ${room.name}`}
                    data-testid={`edit-room-${room.id}`}
                    onClick={() => startEditRoom(room)}
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${room.name}`}
                    data-testid={`delete-room-${room.id}`}
                    onClick={() => setRoomPendingDelete(room)}
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-destructive/10 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col" data-testid={`room-items-list-${room.id}`}>
                {room.items.filter((i) => !i.deletedAt).length === 0 ? (
                  <p className="px-3 py-3 text-xs text-muted-foreground">No items yet.</p>
                ) : (
                  room.items
                    .filter((i) => !i.deletedAt)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border last:border-b-0"
                        data-testid={`item-row-${item.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {ITEM_CATEGORY_LABELS[item.category]}
                            {item.category === 'APPLIANCE' && (item.make || item.model || item.serialNumber) && (
                              <> · {[item.make, item.model, item.serialNumber && `SN ${item.serialNumber}`].filter(Boolean).join(' ')}</>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            aria-label={`Edit ${item.name}`}
                            data-testid={`edit-item-${item.id}`}
                            onClick={() => startEditItem(item)}
                            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete ${item.name}`}
                            data-testid={`delete-item-${item.id}`}
                            onClick={() => setItemPendingDelete(item)}
                            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-destructive/10 text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                )}
                <button
                  type="button"
                  data-testid={`add-item-button-${room.id}`}
                  onClick={() => openAddItem(room.id)}
                  className="flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] text-sm text-primary hover:bg-accent"
                >
                  <Plus className="h-4 w-4" /> Add Item
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Room modal */}
      <Modal
        isOpen={isAddRoomOpen}
        onClose={() => setIsAddRoomOpen(false)}
        title="Add Room"
        data-testid="add-room-modal"
      >
        <form onSubmit={handleAddRoomSubmit} data-testid="add-room-form">
          {roomFormError && (
            <div className="p-3 mb-4 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive" role="alert">
              {roomFormError}
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="room-type-select" className={labelClass}>Room Type *</label>
            <select
              id="room-type-select"
              data-testid="room-type-select"
              className={inputClass}
              value={roomForm.roomType}
              onChange={(e) => handleRoomTypeSelect(e.target.value as RoomType)}
              required
              aria-required="true"
            >
              <option value="">Select a room type</option>
              {ROOM_TYPE_OPTIONS.map((rt) => (
                <option key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="room-name-input" className={labelClass}>Room Name *</label>
            <input
              id="room-name-input"
              data-testid="room-name-form-input"
              type="text"
              className={inputClass}
              value={roomForm.name}
              onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
              required
              aria-required="true"
              maxLength={255}
            />
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                id="populate-defaults-checkbox"
                data-testid="populate-defaults-checkbox"
                checked={effectivePopulateDefaults}
                disabled={!selectedRoomTypeHasDefaults}
                onChange={(e) => setRoomForm({ ...roomForm, populateDefaults: e.target.checked })}
                className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
              />
              Add standard items for this room type
              {selectedRoomTypeHasDefaults && (
                <span className="text-xs text-muted-foreground">
                  ({selectedRoomTypeDefault!.defaultItems.length} item{selectedRoomTypeDefault!.defaultItems.length !== 1 ? 's' : ''})
                </span>
              )}
            </label>
            {roomForm.roomType && !selectedRoomTypeHasDefaults && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="populate-defaults-hint">
                No standard items for this room type.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setIsAddRoomOpen(false)} disabled={isSavingRoom}>Cancel</Button>
            <Button type="submit" disabled={isSavingRoom || !roomForm.roomType || !roomForm.name.trim()} data-testid="add-room-submit">
              {isSavingRoom ? 'Adding…' : 'Add Room'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add/Edit Item modal */}
      <Modal
        isOpen={addItemRoomId !== null || editingItem !== null}
        onClose={() => { setAddItemRoomId(null); setEditingItem(null); }}
        title={editingItem ? 'Edit Item' : 'Add Item'}
        data-testid="item-form-modal"
      >
        <form onSubmit={editingItem ? handleEditItemSubmit : handleAddItemSubmit} data-testid="item-form">
          {itemFormError && (
            <div className="p-3 mb-4 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive" role="alert">
              {itemFormError}
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="item-name-input" className={labelClass}>Item Name *</label>
            <input
              id="item-name-input"
              data-testid="item-name-input"
              type="text"
              className={inputClass}
              value={itemForm.name}
              onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
              required
              aria-required="true"
              maxLength={255}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="item-category-select" className={labelClass}>Category *</label>
            <select
              id="item-category-select"
              data-testid="item-category-select"
              className={inputClass}
              value={itemForm.category}
              onChange={(e) => setItemForm({ ...itemForm, category: e.target.value as ItemCategory })}
              required
              aria-required="true"
            >
              <option value="">Select a category</option>
              {ITEM_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{ITEM_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          {itemForm.category === 'APPLIANCE' && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="item-make-input" className={labelClass}>Make</label>
                <input
                  id="item-make-input"
                  data-testid="item-make-input"
                  type="text"
                  className={inputClass}
                  value={itemForm.make}
                  onChange={(e) => setItemForm({ ...itemForm, make: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="item-model-input" className={labelClass}>Model</label>
                <input
                  id="item-model-input"
                  data-testid="item-model-input"
                  type="text"
                  className={inputClass}
                  value={itemForm.model}
                  onChange={(e) => setItemForm({ ...itemForm, model: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="item-serial-input" className={labelClass}>Serial Number</label>
                <input
                  id="item-serial-input"
                  data-testid="item-serial-input"
                  type="text"
                  className={inputClass}
                  value={itemForm.serialNumber}
                  onChange={(e) => setItemForm({ ...itemForm, serialNumber: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => { setAddItemRoomId(null); setEditingItem(null); }} disabled={isSavingItem}>Cancel</Button>
            <Button type="submit" disabled={isSavingItem || !itemForm.category || !itemForm.name.trim()} data-testid="item-form-submit">
              {isSavingItem ? 'Saving…' : editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={roomPendingDelete !== null}
        onClose={() => setRoomPendingDelete(null)}
        onConfirm={confirmDeleteRoom}
        title="Delete Room"
        message={`Are you sure you want to delete "${roomPendingDelete?.name}"? Its items will also be removed. This action cannot be undone.`}
        confirmText="Delete Room"
        isDestructive
        isLoading={isDeletingRoom}
      />

      <ConfirmDialog
        isOpen={itemPendingDelete !== null}
        onClose={() => setItemPendingDelete(null)}
        onConfirm={confirmDeleteItem}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemPendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete Item"
        isDestructive
        isLoading={isDeletingItem}
      />
    </div>
  );
}

function ConditionTemplatePageSkeleton() {
  return (
    <div className="max-w-3xl mx-auto pb-6" data-testid="condition-template-page">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-24 mt-2" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </div>
  );
}

export default ConditionTemplatePage;
